import axios from 'axios'
import { useAuthStore } from '@/stores/auth'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Flag pour éviter les appels multiples de refresh
let isRefreshing = false
let lastRefreshFailureAt: number | null = null
const REFRESH_COOLDOWN_MS = 30_000

// Intercepteur pour ajouter le token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Intercepteur pour gérer les erreurs d'authentification
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Si Axios n'a pas de config (cas rare), ne rien tenter
    if (!originalRequest) {
      return Promise.reject(error)
    }

    // Ne JAMAIS intercepter les requêtes d'authentification
    if (originalRequest.url?.includes('/auth/')) {
      return Promise.reject(error)
    }

    // Si le token est expiré (401 ou 403) et qu'on n'a pas déjà essayé
    const status = error.response?.status
    const now = Date.now()
    const inCooldown = lastRefreshFailureAt !== null && now - lastRefreshFailureAt < REFRESH_COOLDOWN_MS

    if ((status === 401 || status === 403) && !originalRequest._retry && !isRefreshing && !inCooldown) {
      originalRequest._retry = true

      const token = localStorage.getItem('token')

      // Ne jamais tenter un refresh s'il n'y a pas de token
      if (!token) {
        return Promise.reject(error)
      }

      isRefreshing = true
      try {
        const authStore = useAuthStore()
        const refreshed = await authStore.refreshToken()

        if (refreshed) {
          // Réessayer la requête originale avec le nouveau token
          return api(originalRequest)
        }

        // Refresh impossible : activer un cooldown pour éviter les boucles
        lastRefreshFailureAt = Date.now()
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default api
